/**
 * Симуляция «Взаимодействие тел» — опыты 2 и 3 из описания коллеги (TEOR-12). Между двумя
 * тележками — сжатая пружина, стянутая нитью; нить пережигают. Едут обе тележки: пружина
 * действует на обе (взаимодействие). Разгон занимает время (инертность), видно по кадрам
 * стробоскопа в замедлении и по графику скорости. Нагруженная тележка едет медленнее:
 * v₁ / v₂ = m₂ / m₁ — масса есть мера инертности.
 *
 * Пружина прикреплена к первой тележке и упирается во вторую (толькоТолкает): распрямившись,
 * она отходит. Сверка с законом сохранения импульса — tests/pesochnica.test.ts и
 * tests/simulyacii.test.ts.
 */
import type { Модель } from '../pesochnica/model.ts'
import { G_ЗЕМЛЯ, type Сцена } from '../pesochnica/scena.ts'
import type { Итог, Симуляция, Значения } from './opisanie.ts'

const ШИРИНА = 0.2
const КУЗОВ = 0.06
const КОЛЕСО = 0.025
/** Масса пустой тележки и одной гири, кг. */
export const ТЕЛЕЖКА = 0.5
export const ГИРЯ = 0.5
/** Длина пружины без нагрузки, м. */
const ПРУЖИНА = 0.1
const СТОЛ = 3

function число(з: Значения, ключ: string, запас: number): number {
  const v = з[ключ]
  return typeof v === 'number' ? v : запас
}

export function массы(з: Значения): [number, number] {
  return [ТЕЛЕЖКА + ГИРЯ * число(з, 'гири1', 0), ТЕЛЕЖКА + ГИРЯ * число(з, 'гири2', 0)]
}

export function сценаТележек(з: Значения): Сцена {
  const [m1, m2] = массы(з)
  const k = число(з, 'жёсткость', 80)
  const сжатие = число(з, 'сжатие', 0.05)
  const покрытие = typeof з.покрытие === 'string' ? з.покрытие : 'дерево'
  const зазор = ПРУЖИНА - сжатие
  const y = КУЗОВ / 2 + КОЛЕСО
  return {
    версия: 1,
    название: 'Тележки и пружина',
    мир: { g: G_ЗЕМЛЯ },
    поверхности: [
      {
        id: 'стол',
        точки: [[-СТОЛ, 0], [СТОЛ, 0]],
        материал: 'дерево',
        покрытия: покрытие === 'дерево' ? [] : [{ от: -СТОЛ, до: СТОЛ, материал: покрытие }],
        толщина: 0.05,
      },
      { id: 'пол', точки: [[-СТОЛ - 2, -0.8], [СТОЛ + 2, -0.8]], материал: 'дерево', толщина: 0.05 },
    ],
    тела: [
      {
        id: 'A', форма: 'тележка', ширина: ШИРИНА, высота: КУЗОВ, радиусКолеса: КОЛЕСО,
        x: -(ШИРИНА + зазор) / 2, y, масса: m1, материал: 'пластик', гири: число(з, 'гири1', 0), подпись: 'тележка 1',
      },
      {
        id: 'B', форма: 'тележка', ширина: ШИРИНА, высота: КУЗОВ, радиусКолеса: КОЛЕСО,
        x: (ШИРИНА + зазор) / 2, y, масса: m2, материал: 'пластик', гири: число(з, 'гири2', 0), подпись: 'тележка 2',
      },
    ],
    связи: [
      { id: 'пружина', вид: 'пружина', a: { тело: 'A', точка: [ШИРИНА / 2, 0] }, b: { тело: 'B', точка: [-ШИРИНА / 2, 0] }, k, длина: ПРУЖИНА, толькоТолкает: true },
      { id: 'нить', вид: 'нить', a: { тело: 'A', точка: [ШИРИНА / 2, 0.02] }, b: { тело: 'B', точка: [-ШИРИНА / 2, 0.02] }, длина: зазор },
    ],
  }
}

/** Наибольшая скорость тележки за опыт — сразу после того, как пружина её оттолкнула. */
export function наибольшаяСкорость(м: Модель, id: string): number {
  return Math.max(0, ...м.журнал(id).map((о) => Math.hypot(о.vx, о.vy)))
}

const ru = (x: number, з = 2) => x.toFixed(з).replace('.', ',')

const скорости: Итог = {
  подпись: 'скорость после толчка: тележка 1 · тележка 2',
  значение: (м) => {
    const [a, b] = [наибольшаяСкорость(м, 'A'), наибольшаяСкорость(м, 'B')]
    return a < 0.005 && b < 0.005 ? null : `${ru(a)} м/с · ${ru(b)} м/с`
  },
}

const отношение: Итог = {
  подпись: 'v₁ / v₂   и   m₂ / m₁',
  значение: (м, _с, з) => {
    const [a, b] = [наибольшаяСкорость(м, 'A'), наибольшаяСкорость(м, 'B')]
    if (a < 0.005 || b < 0.005) return null
    const [m1, m2] = массы(з)
    return `${ru(a / b)}   и   ${ru(m2 / m1)}`
  },
}

const пути: Итог = {
  подпись: 'путь: тележка 1 · тележка 2',
  значение: (_м, с) => {
    const [a, b] = ['A', 'B'].map((id) => с.тела.find((т) => т.id === id))
    if (!a || !b || (a.путь < 0.001 && b.путь < 0.001)) return null
    return `${ru(a.путь)} м · ${ru(b.путь)} м`
  },
}

// видно 2,4 м стола: разъехавшиеся тележки остаются в кадре; сцена на компьютере вытянута 3 : 1
const ВИД = { высота: 0.4, низ: -0.16, x0: 0, центрX: 0, ширина: 2.4, пропорция: 3, кадров: 16 }
const РАЗМЕТКА = { y: 0, от: -СТОЛ, до: СТОЛ, ноль: 0, шаг: 0.05, подпись: 0.25, толщина: 0.05 }
const ПОКРЫТИЕ = {
  вид: 'выбор', ключ: 'покрытие', подпись: 'Стол',
  варианты: [
    { значение: 'дерево', подпись: 'дерево' },
    { значение: 'песок', подпись: 'песок' },
    { значение: 'гладь', подпись: 'идеально гладкая поверхность' },
  ],
} as const
const гири = (n: 1 | 2) =>
  ({ вид: 'ползунок', ключ: `гири${n}`, подпись: `Гири на тележке ${n} (по 0,5 кг)`, min: 0, max: 4, шаг: 1, единица: 'шт.', знаков: 0 }) as const
const ПУСК = { пережечь: ['нить'], подпись: '🔥 Пережечь нить' }

export const ВЗАИМОДЕЙСТВИЕ: Симуляция = {
  id: 'vzaimodeystvie',
  название: 'Взаимодействие тел',
  раздел: 'Механика',
  тема: 'Взаимодействие тел. Инертность. Масса',
  класс: 7,
  кратко: 'две тележки, пружина и нить: кто поедет, сколько длится разгон, при чём тут масса',
  экраны: [
    {
      id: 'telezhki',
      название: 'Взаимодействие',
      задание: 'Между тележками сжатая пружина, её держит нить. Сначала ответь на вопрос, потом пережги нить.',
      начальные: { покрытие: 'дерево' },
      сцена: сценаТележек,
      управление: [ПОКРЫТИЕ],
      показать: { силы: true, значения: false, скорость: true, стробоскоп: false },
      приборы: ['линейка'],
      вид: ВИД,
      разметка: РАЗМЕТКА,
      пуск: ПУСК,
      итоги: [скорости, пути],
      вопрос: {
        вопрос: 'Пружина прикреплена к первой тележке. Если пережечь нить, поедет…',
        варианты: ['только первая — к ней прикреплена пружина', 'только вторая — её толкает пружина', 'обе тележки'],
        верный: 2,
        пояснение: 'Поехали обе: пружина действует на обе тележки. Если первое тело действует на второе, то и второе действует на первое — в природе существует не действие, а взаимодействие.',
        раскрыть: 'после пуска',
      },
    },
    {
      id: 'razgon',
      название: 'Инертность',
      задание: 'Время идёт в 10 раз медленнее, стробоскоп снимает тележки каждые 0,02 с. Посмотри, как они набирают скорость.',
      начальные: { покрытие: 'дерево' },
      сцена: сценаТележек,
      управление: [ПОКРЫТИЕ],
      показать: { силы: false, значения: false, скорость: true, стробоскоп: true },
      стробоскопШаг: 0.02,
      медленно: true,
      приборы: ['график'],
      график: { величины: ['скорость', 'координата'], сразу: ['скорость'] },
      вид: ВИД,
      разметка: РАЗМЕТКА,
      пуск: ПУСК,
      итоги: [скорости],
      вопрос: {
        вопрос: 'Тележки набрали скорость мгновенно или для этого потребовалось время?',
        варианты: ['мгновенно', 'потребовалось время'],
        верный: 1,
        пояснение: 'Сначала скорость 0, потом чуть больше, потом ещё больше — и только когда пружина распрямилась, скорость перестаёт расти. Чтобы изменить скорость тела, нужно время. Это свойство тела — инертность.',
        раскрыть: 'после пуска',
      },
    },
    {
      id: 'gruzy',
      название: 'Масса — мера инертности',
      задание: 'Поставь гири на одну из тележек и сравни скорости, с которыми они разъедутся.',
      // гладкий стол: на деревянном качение отнимает скорость уже во время толчка, и v₁/v₂ уходит от m₂/m₁
      начальные: { покрытие: 'гладь', гири1: 0, гири2: 2 },
      сцена: сценаТележек,
      управление: [гири(1), гири(2), ПОКРЫТИЕ],
      показать: { силы: false, значения: false, скорость: true, стробоскоп: false },
      приборы: ['линейка', 'график'],
      график: { величины: ['скорость', 'путь'] },
      вид: ВИД,
      разметка: РАЗМЕТКА,
      пуск: ПУСК,
      итоги: [скорости, отношение, пути],
      вопрос: {
        вопрос: 'На вторую тележку поставили гири. Какая тележка поедет быстрее?',
        варианты: ['тяжёлая', 'лёгкая', 'одинаково'],
        верный: 1,
        пояснение: 'Лёгкая едет быстрее: во сколько раз масса больше, во столько раз скорость меньше. У тяжёлой тележки инертность больше — значит, есть величина, которая служит мерой инертности. Это масса.',
        раскрыть: 'после пуска',
      },
    },
    {
      id: 'laboratoriya',
      название: 'Свой опыт',
      задание: 'Меняй грузы, пружину и стол — и проверь, когда отношение скоростей равно обратному отношению масс.',
      начальные: { покрытие: 'гладь', гири1: 1, гири2: 0, жёсткость: 80, сжатие: 0.05 },
      сцена: сценаТележек,
      управление: [
        гири(1),
        гири(2),
        { вид: 'ползунок', ключ: 'жёсткость', подпись: 'Жёсткость пружины', min: 20, max: 200, шаг: 10, единица: 'Н/м', знаков: 0 },
        { вид: 'ползунок', ключ: 'сжатие', подпись: 'Сжатие пружины', min: 0.01, max: 0.06, шаг: 0.005, единица: 'см', множитель: 100, знаков: 1 },
        ПОКРЫТИЕ,
      ],
      показать: { силы: true, значения: true, скорость: true, стробоскоп: false, след: false },
      стробоскопШаг: 0.02,
      приборы: ['линейка', 'секундомер', 'график'],
      график: { величины: ['скорость', 'координата', 'путь'] },
      вид: ВИД,
      разметка: РАЗМЕТКА,
      пуск: ПУСК,
      итоги: [скорости, отношение, пути],
    },
  ],
}
